import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { getEnabledModules } from '../services/moduleLicensingService';
import { getSetupSummary, type OrganizationSetupSummary, type SubscriptionStatus, getActiveSubscription } from '../services/setupCenterService';
import { getLocalization, type HRLocalization } from '../services/localizationService';
import { seedCurrencyFromCode } from '../utils/currencyUtils';

interface AppContextValue {
  // Module licensing — top-level module access (PAYROLL, ATTENDANCE, PERFORMANCE…)
  enabledModules: string[];
  hasModule: (code: string) => boolean;
  modulesLoading: boolean;

  // Plan feature flags — sub-feature access within a module (SALARY_REVISION, ROSTER_PLANNING…)
  // These are separate from module licensing and come from the subscription's EnabledFeatures.
  hasFeature: (code: string) => boolean;

  // Setup Center
  setupSummary: OrganizationSetupSummary | null;
  setupLoading: boolean;
  refreshSetup: () => Promise<void>;

  // Subscription — raw
  subscription: SubscriptionStatus | null;
  isSubscriptionActive: boolean;

  // Subscription — computed (single source of truth for all subscription UI)
  currentPlan: string;
  subscriptionStatus: string | null;
  isTrial: boolean;
  isExpired: boolean;
  canUpgrade: boolean;
  trialDaysRemaining: number | null;

  // Localization
  localization: HRLocalization | null;
  currency: string;
  payrollFrequency: string;

  // App-ready flag — true once modules + setup have both resolved
  appReady: boolean;

  // Manual refresh
  refreshAll: () => Promise<void>;
}

const AppContext = createContext<AppContextValue>({
  enabledModules: [],
  hasModule: () => false,
  modulesLoading: true,
  hasFeature: () => false,
  setupSummary: null,
  setupLoading: true,
  refreshSetup: async () => { },
  subscription: null,
  isSubscriptionActive: false,
  currentPlan: 'Plan',
  subscriptionStatus: null,
  isTrial: false,
  isExpired: false,
  canUpgrade: false,
  trialDaysRemaining: null,
  localization: null,
  currency: 'PKR',
  payrollFrequency: 'Monthly',
  appReady: false,
  refreshAll: async () => { },
});

export const useAppContext = () => useContext(AppContext);

const calculateTrialDaysRemaining = (subscription: any, isTrial: boolean): number | null => {
  if (!isTrial) return null;
  // Use the server-computed ceiling value when available (avoids client TZ drift).
  if (subscription?.trialExpiresInDays !== undefined && subscription.trialExpiresInDays !== null)
    return subscription.trialExpiresInDays;
  if (!subscription?.trialEndDate) return null;
  const diff = new Date(subscription.trialEndDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((s: RootState) => (s.auth as any).isAuthenticated);
  // Read the role from redux (reliable — localStorage can lag right after login).
  const authRole = useSelector((s: RootState) =>
    (((s.auth as any).activeRole || (s.auth as any).user?.role || '') as string).toLowerCase().trim());

  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);

  const [setupSummary, setSetupSummary] = useState<OrganizationSetupSummary | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);

  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [localization, setLocalization] = useState<HRLocalization | null>(null);
  const [appReady, setAppReady] = useState(false);

  // Prevent duplicate calls on rapid re-renders
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;

    // The platform super-admin is org-less with its own dedicated UI: tenant-scoped
    // calls below would 403 (no OrganizationId). Skip them so the app is ready cleanly.
    if (authRole === 'platform') {
      setEnabledModules([]);
      setModulesLoading(false);
      setSetupLoading(false);
      setAppReady(true);
      return;
    }

    // Run all three in parallel — independent calls
    const [modulesResult, setupResult, subResult, locResult] = await Promise.allSettled([
      getEnabledModules(),
      getSetupSummary(),
      getActiveSubscription(),
      getLocalization(),
    ]);

    if (modulesResult.status === 'fulfilled') {
      setEnabledModules(modulesResult.value.enabledModules ?? []);
    }
    setModulesLoading(false);

    if (setupResult.status === 'fulfilled') {
      setSetupSummary(setupResult.value);
    }
    setSetupLoading(false);

    if (subResult.status === 'fulfilled') {
      setSubscription(subResult.value);
    }

    if (locResult.status === 'fulfilled') {
      setLocalization(locResult.value);
      // Seed localStorage so formatCurrency() / formatPKR() pick up the org's currency
      // without a page reload. Must run before appReady = true.
      seedCurrencyFromCode(locResult.value.defaultCurrency);
    }

    // All parallel calls settled — app is ready to render
    setAppReady(true);
  }, [isAuthenticated, authRole]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset on logout
      setEnabledModules([]);
      setSetupSummary(null);
      setSubscription(null);
      setLocalization(null);
      setModulesLoading(true);
      setSetupLoading(true);
      setAppReady(false);
      loadedRef.current = false;
      return;
    }

    if (loadedRef.current) return;
    loadedRef.current = true;
    load();
  }, [isAuthenticated, load]);

  const refreshSetup = useCallback(async () => {
    setSetupLoading(true);
    try {
      const { refreshSetupCache } = await import('../services/setupCenterService');
      const fresh = await refreshSetupCache();
      setSetupSummary(fresh);
    } finally {
      setSetupLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    loadedRef.current = false;
    await load();
  }, [load]);

  const isSubscriptionActive = subscription?.status === 'Active';

  // Both checks require an active subscription — a cached/stale enabledFeatures or
  // enabledModules list must not keep unlocking access once a plan expires or is
  // suspended, even if the backend hasn't cleared those arrays yet.
  const hasModule = useCallback(
    (code: string) => isSubscriptionActive && enabledModules.includes(code),
    [enabledModules, isSubscriptionActive],
  );

  const hasFeature = useCallback(
    (code: string) => isSubscriptionActive && (subscription?.enabledFeatures?.includes(code) ?? false),
    [subscription, isSubscriptionActive],
  );

  // ── Computed subscription fields ──────────────────────────────────────────
  const currentPlan = subscription?.planCode ?? 'Plan';
  const subscriptionStatus = subscription?.status ?? null;
  const isTrial = subscription?.isTrial ?? false;

  const isExpired =
    subscription?.status === 'Expired' ||
    subscription?.status === 'TrialExpired' ||
    subscription?.status === 'Suspended';

  const canUpgrade = isTrial || isExpired || subscription?.status === 'Suspended';

  const trialDaysRemaining = calculateTrialDaysRemaining(subscription, isTrial);

  return (
    <AppContext.Provider
      value={{
        enabledModules,
        hasModule,
        modulesLoading,
        hasFeature,
        setupSummary,
        setupLoading,
        refreshSetup,
        subscription,
        isSubscriptionActive,
        currentPlan,
        subscriptionStatus,
        isTrial,
        isExpired,
        canUpgrade,
        trialDaysRemaining,
        localization,
        currency: localization?.defaultCurrency ?? 'PKR',
        payrollFrequency: localization?.defaultPayrollFrequency ?? 'Monthly',
        appReady,
        refreshAll,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
